import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertCircle,
  RefreshCw,
  Monitor,
  Users,
  TrendingUp,
  Heart,
  Thermometer,
  Gauge
} from 'lucide-react';
import apiClient from '../../../services/apiClient';
import toast from 'react-hot-toast';

const ConnexDeviceMonitor = () => {
  const [recentVitals, setRecentVitals] = useState([]);
  const [stats, setStats] = useState({
    total_messages: 0,
    successful_messages: 0,
    failed_messages: 0,
    active_devices: 0,
    last_message_time: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    document.title = 'MediFusion - Connex Device Monitor';
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch endpoints individually with fallbacks for better error handling
      const promises = [
        apiClient.get('/connex-integration/vitals/recent?limit=10').catch(e => ({ data: [] })),
        apiClient.get('/connex-integration/stats').catch(e => ({ 
          data: { total_messages: 0, successful_messages: 0, failed_messages: 0, active_devices: 0, last_message_time: null }
        }))
      ];
      
      const [vitalsResponse, statsResponse] = await Promise.all(promises);
      
      setRecentVitals(Array.isArray(vitalsResponse.data) ? vitalsResponse.data : []);
      setStats(statsResponse.data || { 
        total_messages: 0, 
        successful_messages: 0, 
        failed_messages: 0, 
        active_devices: 0, 
        last_message_time: null 
      });
      
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching Connex data:', error);
      setError('Some Connex data could not be loaded. Backend deployment may be in progress.');
      setLoading(false);
      if (!loading) { // Only show toast if not initial loading
        toast.error('Some Connex data could not be loaded');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      // Format to local timezone with full date and time
      return date.toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney', // Adjust to your local timezone
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeString;
    }
  };

  const calculateSuccessRate = () => {
    if (stats.total_messages === 0) return 0;
    return Math.round((stats.successful_messages / stats.total_messages) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Connex Device Monitor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Reduced height by 40% */}
      <div className="bg-blue-600 text-white py-2.5 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <Activity className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Connex Device Integration Monitor</h1>
          </div>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 rounded-md transition-colors disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Status Alert - More compact */}
        <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Monitor className="h-4 w-4 text-blue-600" />
            <p className="text-blue-800 text-sm">
              Medical devices connect via TCP on port 3100. Health monitoring available on port 3101.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Messages Processed</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_messages}</p>
              </div>
              <Activity className="h-10 w-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-3xl font-bold text-green-600">{calculateSuccessRate()}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed Messages</p>
                <p className="text-3xl font-bold text-red-600">{stats.failed_messages}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Devices</p>
                <p className="text-3xl font-bold text-blue-600">{stats.active_devices}</p>
              </div>
              <Monitor className="h-10 w-10 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Recent Vital Signs */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <Heart className="h-6 w-6 text-red-500" />
              <span>Recent Vital Signs from Connex Devices</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    BP
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HR
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Temp
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SpO2
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RR
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Glucose
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Height
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weight
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recorded
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentVitals.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center space-y-2">
                        <Heart className="h-12 w-12 text-gray-400" />
                        <p>No vital signs data available yet</p>
                        <p className="text-sm">Vital signs will appear here when devices send data</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentVitals.map((vital) => (
                    <tr key={vital.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {vital.patient_name || (vital.patient?.first_name + ' ' + vital.patient?.last_name) || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900 flex items-center justify-center space-x-1">
                          {vital.systolic_bp && vital.diastolic_bp ? (
                            <>
                              <Activity className="h-4 w-4 text-red-500" />
                              <span>{vital.systolic_bp}/{vital.diastolic_bp}</span>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900 flex items-center justify-center space-x-1">
                          {vital.pulse_rate ? (
                            <>
                              <Heart className="h-4 w-4 text-pink-500" />
                              <span>{vital.pulse_rate}</span>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900 flex items-center justify-center space-x-1">
                          {vital.temperature ? (
                            <>
                              <Thermometer className="h-4 w-4 text-blue-500" />
                              <span>{vital.temperature}°C</span>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900 flex items-center justify-center space-x-1">
                          {vital.spo2 ? (
                            <>
                              <Gauge className="h-4 w-4 text-green-500" />
                              <span>{vital.spo2}%</span>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">
                          {vital.respiratory_rate ? `${vital.respiratory_rate}` : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">
                          {vital.glucose ? `${vital.glucose}` : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">
                          {vital.height ? `${vital.height}` : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">
                          {vital.weight ? `${vital.weight}` : '-'}
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-center">
                        <div className="text-xs text-gray-900 break-all max-w-32" title={vital.device_id}>
                          {vital.device_id || '-'}
                        </div>
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap text-center">
                        <div className="text-xs text-gray-500">
                          {formatDateTime(vital.device_recorded_at || vital.recorded_at)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Last Update Info */}
        {stats.last_message_time && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Last message received: {formatDateTime(stats.last_message_time)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnexDeviceMonitor;
